import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Search, Loader2, User, Building2, Landmark, Phone, Mail, MapPin, Plus, X, AlertTriangle } from "lucide-react";

const TYPE_META = {
  individual: { label: "Individual / Farmer", icon: User, dot: "bg-green-500", tint: "bg-[#39FF14]/10 text-[#39FF14] border-[#39FF14]/25" },
  business: { label: "Business / Organization", icon: Building2, dot: "bg-amber-500", tint: "bg-yellow-500/15 text-yellow-200 border-yellow-500/30" },
  government: { label: "Government / Agency", icon: Landmark, dot: "bg-red-500", tint: "bg-red-500/15 text-red-300 border-red-500/25" },
};

function escapeRegex(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function Highlight({ text, query }) {
  if (!text) return null;
  const q = (query || "").trim();
  if (!q) return <>{text}</>;
  const re = new RegExp(`(${escapeRegex(q)})`, "ig");
  const parts = String(text).split(re);
  return (
    <>
      {parts.map((p, i) =>
        p.toLowerCase() === q.toLowerCase()
          ? <mark key={i} className="bg-amber-200/70 text-[#F8F9FA] rounded-sm px-0.5">{p}</mark>
          : <span key={i}>{p}</span>
      )}
    </>
  );
}

/**
 * Global reusable customer search / picker.
 *
 * Props:
 *   value           — currently-selected customer id
 *   onChange(c)     — called with the full customer object when a result is chosen
 *   onCreateNew()   — optional callback for the "+ Create New Customer" button.
 *                     If omitted, the button links to /customers.
 *   placeholder     — optional input placeholder
 *   testid          — optional data-testid stub
 *   customer        — optional pre-loaded customer object for the current value
 *                     (avoids a round-trip to render the chip on first mount)
 *   disabled        — disable the whole input
 *   autoFocus       — focus the input on mount
 */
export default function CustomerSearch({
  value, onChange, onCreateNew, placeholder = "Search customer by name, TZAT, mobile, org…",
  testid = "customer-search", customer, disabled, autoFocus,
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [selected, setSelected] = useState(customer || null);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  // If the caller hydrates `customer` later, sync it.
  useEffect(() => {
    if (customer && customer.id !== selected?.id) setSelected(customer);
    // eslint-disable-next-line
  }, [customer?.id]);

  // If value cleared externally, drop the chip.
  useEffect(() => {
    if (!value) setSelected(null);
  }, [value]);

  // Fetch when query changes (debounced).
  const fetchResults = useCallback(async (q) => {
    if (!q || q.trim().length < 1) { setResults([]); setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/customers/search?q=${encodeURIComponent(q.trim())}`);
      setResults(data || []);
    } catch (e) {
      setResults([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchResults(query), 180);
    return () => clearTimeout(debounceRef.current);
  }, [query, fetchResults]);

  // Reset highlight index when results change.
  useEffect(() => { setHighlight(-1); }, [results]);

  // Close on outside click.
  useEffect(() => {
    const onDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const choose = (c) => {
    setSelected(c);
    setOpen(false);
    setQuery("");
    setResults([]);
    onChange?.(c);
  };

  const clear = () => {
    setSelected(null);
    setQuery("");
    setResults([]);
    setOpen(true);
    onChange?.(null);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(h + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(h - 1, -1)); }
    else if (e.key === "Enter") {
      if (highlight >= 0 && highlight < results.length) {
        e.preventDefault();
        choose(results[highlight]);
      }
    } else if (e.key === "Escape") { e.preventDefault(); setOpen(false); }
  };

  const showEmpty = useMemo(() => open && query.trim().length >= 1 && !loading && results.length === 0, [open, query, loading, results]);

  // Selected chip presentation
  if (selected) {
    const type = TYPE_META[selected.customer_type] || TYPE_META.individual;
    const displayName = selected.customer_type === "business" ? (selected.organization_name || selected.name)
      : selected.customer_type === "government" ? (selected.department_name || selected.name)
      : selected.name;
    const TypeIcon = type.icon;
    return (
      <div className="border border-[#3A4640] rounded-md bg-[#121815] p-3 flex items-start gap-3" data-testid={`${testid}-selected`}>
        <div className="w-10 h-10 rounded flex items-center justify-center bg-[#121815] shrink-0">
          <TypeIcon size={18} className="text-[#F8F9FA]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-2">
            <div className="font-heading font-bold text-[#F8F9FA] truncate">{displayName || "(no name)"}</div>
            <span className={`inline-flex items-center gap-1 text-[10px] font-heading px-1.5 py-0.5 rounded border ${type.tint}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${type.dot}`} /> {type.label.split(" / ")[0]}
            </span>
          </div>
          <div className="text-[11px] font-mono text-[#8A8F98]">{selected.tzat_no}</div>
          <div className="text-xs text-[#8A8F98] mt-0.5 flex flex-wrap gap-x-3">
            {selected.phone && <span className="flex items-center gap-1"><Phone size={10} /> {selected.phone}</span>}
            {selected.email && <span className="flex items-center gap-1 text-[#8A8F98]"><Mail size={10} /> {selected.email}</span>}
            {(selected.village || selected.district) && <span className="flex items-center gap-1 text-[#8A8F98]"><MapPin size={10} /> {[selected.village, selected.district].filter(Boolean).join(" · ")}</span>}
          </div>
        </div>
        {!disabled && (
          <button type="button" onClick={clear} className="text-[#8A8F98] hover:text-red-600 p-1" title="Change customer" data-testid={`${testid}-clear`}>
            <X size={14} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative" data-testid={testid}>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8F98] pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          disabled={disabled}
          autoFocus={autoFocus}
          autoComplete="off"
          placeholder={placeholder}
          data-testid={`${testid}-input`}
          className="w-full h-10 pl-9 pr-9 bg-[#121815] border border-[#3A4640] rounded-md text-sm text-[#F8F9FA] placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-[#166534] focus:border-[#39FF14]/40 transition-shadow"
        />
        {loading && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A8F98] animate-spin" />}
      </div>

      {open && (loading || results.length > 0 || showEmpty) && (
        <div
          className="absolute z-50 mt-1.5 left-0 right-0 bg-[#121815] border border-[#3A4640] rounded-md shadow-xl max-h-[380px] overflow-y-auto"
          data-testid={`${testid}-dropdown`}
        >
          {loading && results.length === 0 && (
            <div className="px-4 py-6 text-sm text-[#8A8F98] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Searching…</div>
          )}
          {results.map((c, idx) => {
            const type = TYPE_META[c.customer_type] || TYPE_META.individual;
            const TypeIcon = type.icon;
            const isHighlighted = idx === highlight;
            const displayName = c.customer_type === "business" ? (c.organization_name || c.name)
              : c.customer_type === "government" ? (c.department_name || c.name)
              : c.name;
            const subLine = c.customer_type === "business" ? (c.contact_person || "")
              : c.customer_type === "government" ? (c.office_division || "")
              : (c.farmer_id ? `Farmer ID · ${c.farmer_id}` : "");
            return (
              <button
                type="button"
                key={c.id}
                onMouseEnter={() => setHighlight(idx)}
                onClick={() => choose(c)}
                data-testid={`${testid}-result-${c.id}`}
                className={`w-full text-left px-4 py-3 border-b border-[#3A4640] last:border-0 flex gap-3 items-start transition-colors ${isHighlighted ? "bg-green-50" : "hover:bg-[#0B0F0C]"}`}
              >
                <div className={`w-9 h-9 rounded flex items-center justify-center shrink-0 ${isHighlighted ? "bg-[#39FF14] text-[#0B0F0C]" : "bg-[#121815] text-[#F8F9FA]"}`}>
                  <TypeIcon size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center flex-wrap gap-2">
                    <div className="font-heading font-bold text-[#F8F9FA] truncate">
                      <Highlight text={displayName || "(no name)"} query={query} />
                    </div>
                    <span className={`inline-flex items-center gap-1 text-[10px] font-heading px-1.5 py-0.5 rounded border ${type.tint}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${type.dot}`} /> {type.label.split(" / ")[0]}
                    </span>
                    {c.incomplete && (
                      <span className="text-[9px] uppercase tracking-wider bg-yellow-500/15 text-yellow-200 border border-yellow-500/30 px-1 py-0.5 rounded font-heading flex items-center gap-0.5">
                        <AlertTriangle size={8} /> Incomplete
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] font-mono text-[#8A8F98] mt-0.5">
                    <Highlight text={c.tzat_no} query={query} />
                  </div>
                  {subLine && <div className="text-xs text-[#8A8F98] mt-0.5 truncate"><Highlight text={subLine} query={query} /></div>}
                  <div className="text-xs text-[#8A8F98] mt-1 flex flex-wrap gap-x-3">
                    {c.phone && <span className="flex items-center gap-1"><Phone size={10} /> <Highlight text={c.phone} query={query} /></span>}
                    {c.alternate_phone && <span className="flex items-center gap-1 text-[#8A8F98]"><Phone size={10} /> <Highlight text={c.alternate_phone} query={query} /></span>}
                    {c.email && <span className="flex items-center gap-1 text-[#8A8F98]"><Mail size={10} /> <Highlight text={c.email} query={query} /></span>}
                  </div>
                  {(c.village || c.district) && (
                    <div className="text-xs text-[#8A8F98] mt-0.5 flex items-center gap-1">
                      <MapPin size={10} /> <Highlight text={[c.village, c.district, c.state].filter(Boolean).join(" · ")} query={query} />
                    </div>
                  )}
                </div>
              </button>
            );
          })}

          {showEmpty && (
            <div className="px-4 py-5 text-sm" data-testid={`${testid}-empty`}>
              <div className="text-[#F8F9FA] mb-2">No customer found for <b>&ldquo;{query}&rdquo;</b>.</div>
              <button
                type="button"
                onClick={() => { setOpen(false); onCreateNew ? onCreateNew(query) : window.location.assign(`/customers?new=1`); }}
                data-testid={`${testid}-create-new`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#39FF14] hover:bg-[#2ECC71] text-[#0B0F0C] text-xs font-heading font-semibold uppercase tracking-wider rounded-md transition-colors"
              >
                <Plus size={13} /> Create New Customer
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
